// src/pages/VehicleV2/BillAddAdvance.jsx
// Design aligned with ProductionBOMListing
// Type 1 = Advance, Type 2 = Shifting
// Writes into billGen_selection_${vehicleId} so Bill Generation Entry can pick it up

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaArrowLeft,
  FaSave,
  FaTrash,
  FaFileInvoiceDollar,
  FaSyncAlt,
  FaEdit,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";
const API_BASE = `${BASE_URL}/api/billAddAdvance`;

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};
const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();
const isValidDDMMYYYY = (s) =>
  typeof s === "string" && /^(\d{2})\/(\d{2})\/(\d{4})$/.test(s);
const pad2 = (n) => String(n).padStart(2, "0");
const isoToDDMMYYYY = (isoOrDateStr) => {
  if (!isoOrDateStr) return "";
  if (isValidDDMMYYYY(isoOrDateStr)) return isoOrDateStr;
  const d = new Date(isoOrDateStr);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};
const ddmmyyyyToInputDate = (ddmmyyyy) => {
  if (!isValidDDMMYYYY(ddmmyyyy)) return "";
  const [dd, mm, yyyy] = ddmmyyyy.split("/");
  return `${yyyy}-${mm}-${dd}`;
};
const inputDateToDDMMYYYY = (yyyyMmDd) => {
  if (!yyyyMmDd) return "";
  const [yyyy, mm, dd] = String(yyyyMmDd).split("-");
  if (!yyyy || !mm || !dd) return "";
  return `${dd}/${mm}/${yyyy}`;
};
const fmtMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return toStr(v);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};
const numOr0 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const defaultForm = { id: 0, dateInput: "", amount: "" };
const mapRow = (r) => ({
  id: toInt(r?.fld_id ?? r?.id ?? 0, 0),
  date: toStr(r?.fld_date ?? r?.date),
  amount: toStr(r?.fld_amount ?? r?.amount),
});

const BillAddAdvance = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "0";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const vehicleId =
    toInt(params.get("VehID")) ||
    toInt(params.get("VehId")) ||
    toInt(params.get("vehId")) ||
    toInt(params.get("vehicleId")) ||
    0;
  const type = toInt(params.get("Type")) || toInt(params.get("type")) || 0;
  const billNo =
    toStr(params.get("billNo")) ||
    toStr(params.get("BillNo")) ||
    toStr(location.state?.billNo) ||
    "";

  const BILL_SEL_KEY = useMemo(() => `billGen_selection_${vehicleId}`, [vehicleId]);
  const STORAGE_KEY = useMemo(
    () => `billGen_advshifting_${vehicleId}_${type}`,
    [vehicleId, type]
  );

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const [pageLoading, setPageLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [savingLoading, setSavingLoading] = useState(false);
  const [deletingLoading, setDeletingLoading] = useState(false);
  const [showForm, setShowForm] = useState(true);

  const [pageTitle, setPageTitle] = useState("Bill Add Advance / Shifting");
  const [form, setForm] = useState(defaultForm);
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const isBusy = pageLoading || gridLoading || billingLoading || savingLoading || deletingLoading;

  const fieldClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;
  const panelClass = `p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`;
  const panelTitleClass = `text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`;
  const ghostBtn = `flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
    isDark
      ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
  }`;
  const primaryBtn = `flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`;

  const checkedCount = useMemo(() => Object.keys(selected).length, [selected]);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(toInt(total, 0) / pageSize)), [total, pageSize]);
  const entryTypeLabel = type === 2 ? "Shifting" : type === 1 ? "Advance" : "-";

  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  const clearForm = useCallback(() => setForm(defaultForm), []);
  const getRowId = useCallback((row, idx) => toInt(row?.id ?? row?.fld_id ?? idx + 1, 0), []);

  const mergeBillSelection = useCallback(
    (ids, totalAmount) => {
      let existing = {};
      try {
        existing = JSON.parse(localStorage.getItem(BILL_SEL_KEY) || "{}");
      } catch {
        existing = {};
      }
      const next = { ...existing, vehicleId, updatedAt: new Date().toISOString() };
      if (type === 2) {
        next.shiftingIds = ids;
        next.shiftingAmt = String(totalAmount ?? 0);
      } else {
        next.advanceIds = ids;
        next.advanceAmt = String(totalAmount ?? 0);
      }
      localStorage.setItem(BILL_SEL_KEY, JSON.stringify(next));
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ids, totalAmount: String(totalAmount ?? 0), type, vehicleId, billNo })
      );
    },
    [BILL_SEL_KEY, STORAGE_KEY, type, vehicleId, billNo]
  );

  const goToBillGenerationWithData = useCallback(
    (handoff, ids, totalAmount) => {
      mergeBillSelection(ids, totalAmount);
      const payload = { vehicleId, type, billNo, ...handoff };
      if (type === 2) {
        payload.shiftingIds = ids;
        payload.shiftingAmt = String(totalAmount ?? 0);
      } else {
        payload.advanceIds = ids;
        payload.advanceAmt = String(totalAmount ?? 0);
      }
      sessionStorage.setItem("BillGen_AdvanceShiftingHandoff", JSON.stringify(payload));
      navigate(`/vehicle/BillGeneration?VehID=${encodeURIComponent(vehicleId)}&brtype=H`, {
        state: payload,
      });
    },
    [vehicleId, type, billNo, navigate, mergeBillSelection]
  );

  const validateForm = useCallback(() => {
    const ddmmyyyy = inputDateToDDMMYYYY(form.dateInput);
    if (!ddmmyyyy) return canUse(false, "Please enter Date");
    const amt = Number(form.amount);
    if (!form.amount || !Number.isFinite(amt) || amt <= 0) return canUse(false, "Please enter valid Amount");
    return true;
  }, [form, canUse]);

  const loadInit = useCallback(async () => {
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
    if (!canUse(toInt(branchId), "branchId missing")) return;
    if (!canUse(vehicleId, "VehID missing in URL. Use ?VehID=2142&Type=1")) return;
    if (!canUse(type === 1 || type === 2, "Type must be 1 or 2 in URL")) return;

    try {
      setPageLoading(true);
      const { data } = await axios.get(`${API_BASE}/init`, {
        params: { vehicleId, type, branchId, loginUserId, billNo, page: 1, pageSize },
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Init failed");

      setPageTitle(toStr(data?.title) || (type === 1 ? "ADD ADVANCE" : "ADD SHIFTING"));
      const rows = (data?.list || []).map(mapRow);
      setList(rows);
      setTotal(toInt(data?.total, rows.length));
      setPage(toInt(data?.page, 1) || 1);

      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const saved = JSON.parse(raw);
          const ids = Array.isArray(saved?.ids) ? saved.ids : [];
          const map = {};
          ids.forEach((id) => {
            const n = toInt(id, 0);
            if (n > 0) map[n] = { id: n };
          });
          setSelected(map);
        } else setSelected({});
      } catch {
        setSelected({});
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Initialization failed");
    } finally {
      setPageLoading(false);
    }
  }, [loginUserId, branchId, vehicleId, type, billNo, pageSize, commonHeaders, canUse, STORAGE_KEY]);

  const loadList = useCallback(
    async (nextPage = 1) => {
      if (!canUse(vehicleId, "VehID missing in URL")) return;
      if (!canUse(type === 1 || type === 2, "Type must be 1 or 2")) return;
      try {
        setGridLoading(true);
        const { data } = await axios.get(`${API_BASE}/list`, {
          params: { vehicleId, type, branchId, loginUserId, billNo, page: nextPage, pageSize },
          headers: commonHeaders,
        });
        if (!data?.success) {
          setList([]);
          setTotal(0);
          setPage(nextPage);
          toast.warn(data?.message || "No data found");
          return;
        }
        const rows = (data?.list || []).map(mapRow);
        setList(rows);
        setTotal(toInt(data?.total, rows.length));
        setPage(toInt(data?.page, nextPage) || nextPage);
        setSelected({});
      } catch (err) {
        console.error(err);
        setList([]);
        toast.error(err?.response?.data?.message || err.message || "Failed to load list");
      } finally {
        setGridLoading(false);
      }
    },
    [vehicleId, type, branchId, loginUserId, billNo, pageSize, commonHeaders, canUse]
  );

  const handleRefresh = useCallback(async () => {
    clearForm();
    setSelected({});
    await loadInit();
  }, [clearForm, loadInit]);

  const handleBack = useCallback(() => {
    navigate(`/vehicle/BillGeneration?VehID=${vehicleId}&brtype=H`);
  }, [navigate, vehicleId]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;
    if (!canUse(vehicleId, "VehID missing in URL")) return;
    if (!canUse(type === 1 || type === 2, "Type must be 1 or 2")) return;
    const payload = {
      branchId,
      loginUserId,
      vehicleId,
      type,
      billNo,
      id: form.id ? toInt(form.id) : 0,
      date: inputDateToDDMMYYYY(form.dateInput),
      amount: Number(form.amount),
    };
    try {
      setSavingLoading(true);
      const { data } = await axios.post(`${API_BASE}/save`, payload, { headers: commonHeaders });
      if (!data?.success) throw new Error(data?.message || "Save failed");
      toast.success(data?.message || (payload.id ? "Updated successfully" : "Inserted successfully"));
      clearForm();
      await loadList(1);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Save failed");
    } finally {
      setSavingLoading(false);
    }
  }, [validateForm, canUse, vehicleId, type, branchId, loginUserId, billNo, form, commonHeaders, clearForm, loadList]);

  const handleEditRow = useCallback((row) => {
    const id = toInt(row?.id ?? row?.fld_id, 0);
    const ddmmyyyy = isoToDDMMYYYY(row?.date ?? row?.fld_date);
    setForm({
      id,
      dateInput: ddmmyyyyToInputDate(ddmmyyyy),
      amount: toStr(row?.amount ?? row?.fld_amount ?? ""),
    });
    toast.info(`Editing ID: ${id}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const toggleRow = useCallback((row, idx) => {
    const id = getRowId(row, idx);
    setSelected((p) => {
      const next = { ...p };
      if (next[id] !== undefined) delete next[id];
      else next[id] = { id };
      return next;
    });
  }, [getRowId]);

  const allSelected = list.length > 0 && list.every((r, idx) => selected[getRowId(r, idx)] !== undefined);
  const toggleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelected({});
      return;
    }
    const map = {};
    list.forEach((r, idx) => {
      const id = getRowId(r, idx);
      if (id > 0) map[id] = { id };
    });
    setSelected(map);
  }, [allSelected, list, getRowId]);

  const handleDeleteSelected = useCallback(async () => {
    const ids = Object.values(selected).map((x) => toInt(x?.id, 0)).filter((x) => x > 0);
    if (!canUse(ids.length > 0, "Please select any one")) return;
    if (!window.confirm("Are you sure?")) return;
    try {
      setDeletingLoading(true);
      const { data } = await axios.post(
        `${API_BASE}/deleteSelected`,
        { branchId, loginUserId, vehicleId, type, billNo, ids },
        { headers: commonHeaders }
      );
      if (!data?.success) throw new Error(data?.message || "Delete failed");
      toast.success(data?.message || "Deleted Successfully");
      await loadList(page);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Delete failed");
    } finally {
      setDeletingLoading(false);
    }
  }, [selected, canUse, branchId, loginUserId, vehicleId, type, billNo, commonHeaders, loadList, page]);

  const handleBill = useCallback(async () => {
    const ids = Object.values(selected).map((x) => toInt(x?.id, 0)).filter((x) => x > 0);
    if (!canUse(ids.length > 0, "Please select at least one record")) return;
    if (!window.confirm("Add selected rows to the bill?")) return;

    const selectedRows = list.filter((r, idx) => ids.includes(getRowId(r, idx)));
    const totalAmount = selectedRows.reduce((sum, r) => sum + numOr0(r?.amount), 0);

    try {
      setBillingLoading(true);
      const { data } = await axios.post(
        `${API_BASE}/addSelectedToBill`,
        { branchId, loginUserId, vehicleId, type, billNo, ids },
        { headers: commonHeaders }
      );
      if (!data?.success) throw new Error(data?.message || "Add to bill failed");
      toast.success(data?.message || "Added to bill");
      goToBillGenerationWithData(
        {
          selectedIds: ids,
          totalAmount,
          billAdvanceId: data?.billAdvanceId,
          billAdvanceDetId: data?.billAdvanceDetId,
          billAdvanceTotal: data?.billAdvanceTotal ?? totalAmount,
        },
        ids,
        data?.billAdvanceTotal ?? totalAmount
      );
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Add to bill failed");
    } finally {
      setBillingLoading(false);
    }
  }, [selected, canUse, list, getRowId, branchId, loginUserId, vehicleId, type, billNo, commonHeaders, goToBillGenerationWithData]);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">{type === 2 ? "ADD SHIFTING" : "ADD ADVANCE"}</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                {pageTitle}
              </h2>
              <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {branchId || "-"} • User ID: {loginUserId || "-"} • Vehicle ID: {vehicleId || "-"} • Type: {entryTypeLabel} • Selected: {checkedCount}
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button type="button" onClick={handleRefresh} disabled={isBusy} className={`${ghostBtn} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaSyncAlt /> Refresh
              </button>
              <button type="button" onClick={handleBack} disabled={isBusy} className={`${ghostBtn} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaArrowLeft /> Back
              </button>
            </div>
          </div>

          <div className={panelClass}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={panelTitleClass}>
                {entryTypeLabel} Entry
              </h3>
              <button type="button" onClick={() => setShowForm((s) => !s)} className={ghostBtn}>
                {showForm ? <FaChevronUp /> : <FaChevronDown />}
                {showForm ? "Hide" : "Show"}
              </button>
            </div>

            {showForm && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>Date *</label>
                    <input
                      type="date"
                      value={form.dateInput}
                      onChange={(e) => setForm((p) => ({ ...p, dateInput: e.target.value }))}
                      className={fieldClass}
                      disabled={isBusy}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Amount *</label>
                    <input
                      type="number"
                      value={form.amount}
                      onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                      className={fieldClass}
                      placeholder="Enter amount"
                      min="0"
                      step="0.01"
                      disabled={isBusy}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Bill No</label>
                    <div className={fieldClass}>{billNo || "-"}</div>
                  </div>
                  <div>
                    <label className={labelClass}>Vehicle ID</label>
                    <div className={fieldClass}>{vehicleId || "-"}</div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-4">
                  <button type="button" onClick={clearForm} disabled={isBusy} className={ghostBtn}>
                    Clear
                  </button>
                  <button type="button" onClick={handleSubmit} disabled={isBusy} className={`${primaryBtn} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>
                    <FaSave />
                    {savingLoading ? (form.id ? "Updating..." : "Saving...") : form.id ? "Update" : "Submit"}
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {pageLoading || gridLoading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</div>
              </div>
            ) : list.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Records found</div>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="w-4 h-4 cursor-pointer" title="Select All" />
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Date</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Amount</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Edit</th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {list.map((row, idx) => {
                    const id = getRowId(row, idx);
                    const checked = selected[id] !== undefined;
                    return (
                      <tr
                        key={`${id}-${idx}`}
                        className={`${
                          idx % 2 === 0
                            ? isDark ? "bg-gray-800" : "bg-white"
                            : isDark ? "bg-gray-750" : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className="px-6 py-4 text-center">
                          <input type="checkbox" checked={checked} onChange={() => toggleRow(row, idx)} className="w-4 h-4 cursor-pointer" disabled={isBusy} />
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {isoToDDMMYYYY(row?.date) || "-"}
                        </td>
                        <td className={`px-6 py-4 text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>
                          {fmtMoney(row?.amount)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button type="button" onClick={() => handleEditRow(row)} disabled={isBusy} className="text-yellow-500 hover:text-yellow-600 text-lg transition" title="Edit">
                            <FaEdit className="inline" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {list.length > 0 && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-6">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleDeleteSelected}
                  disabled={isBusy || checkedCount === 0}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition bg-red-600 hover:bg-red-700 text-white ${
                    checkedCount === 0 || isBusy ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  <FaTrash /> {deletingLoading ? "Deleting..." : "Remove"}
                </button>
                <button
                  type="button"
                  onClick={handleBill}
                  disabled={isBusy || checkedCount === 0}
                  className={`${primaryBtn} !px-4 !py-2 ${checkedCount === 0 || isBusy ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <FaFileInvoiceDollar /> {billingLoading ? "Processing..." : "Add to Bill"}
                </button>
              </div>
              <div className="flex items-center justify-center gap-2">
                <button
                  className={`px-3 py-1 border rounded ${isDark ? "bg-gray-800 text-white border-gray-700" : "bg-white text-gray-700 border-gray-300"} ${page <= 1 || isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                  onClick={() => loadList(Math.max(1, page - 1))}
                  disabled={page <= 1 || isBusy}
                >
                  Prev
                </button>
                <span className={`px-3 py-1 ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                  Page {page} / {totalPages}
                </span>
                <button
                  className={`px-3 py-1 border rounded ${isDark ? "bg-gray-800 text-white border-gray-700" : "bg-white text-gray-700 border-gray-300"} ${page >= totalPages || isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                  onClick={() => loadList(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages || isBusy}
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {isBusy && (
            <div className={`mt-3 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {pageLoading ? "Loading page..." : gridLoading ? "Loading grid..." : savingLoading ? "Saving..." : deletingLoading ? "Deleting..." : billingLoading ? "Adding to bill..." : "Loading..."}
            </div>
          )}

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default BillAddAdvance;